/** Service Desk console: Ticket workspace (C02, SD-S1-44). Copy follows mockups/tickets/ticket-detail.html. */
export const serviceTicketDetail = {
  label: "Ticket",
  loading: "Loading ticket",
  missing: {
    title: "{{number}} was not found",
    body: "It may have been merged, or you may not have access to it. Go back to the list to find it.",
  },
  error: {
    title: "We couldn't load this ticket",
    body: "Check your connection and try again. Nothing was changed.",
  },
  errors: {
    invalidTransition: "That move isn't allowed for this ticket right now. The status menu lists the moves that are.",
    fieldsRequired: "Some required fields are empty: {{keys}}.",
    mergeOtherOrg: "These tickets belong to different organisations, so they can't be merged.",
  },
  bar: {
    back: "Tickets",
    move: "Move between tickets",
    previous: "Previous ticket (k)",
    next: "Next ticket (j)",
    position: "{{position}} of {{count}}",
    positionOpen: "{{count}} open",
    positionMore: "{{count}}+ open",
    live: { connecting: "Connecting", live: "Live", reconnecting: "Reconnecting" },
  },
  queue: {
    label: "Ticket queue",
    title: "Queue",
    count: "{{count}} open · most urgent first",
  },
  head: {
    copy: "Copy ticket number",
    copied: "{{number}} copied",
    unverified: "Unverified requester",
    unassigned: "Unassigned",
    viewing: "{{name}} is viewing",
    viewingMany: "{{name}} and {{count}} more are viewing",
    merge: "Merge",
    resolve: "Resolve",
    more: "More actions",
    watched: "Watching {{number}}: you will get its updates",
    unwatched: "You stopped watching {{number}}",
    moreMenu: { link: "Link a ticket", watch: "Watch ticket", unwatch: "Stop watching", cancel: "Close as cancelled…" },
    priority: {
      button: "Priority: {{priority}}. Change priority",
      menu: "Set priority",
      changed: "Priority set to {{priority}}. SLA targets recalculated.",
    },
    status: {
      button: "Status: {{status}}. Change status",
      menu: "Change status",
      menuTitle: "Move to (valid transitions only)",
      none: "No moves from “{{status}}”. A client reply to a closed ticket creates a follow-up ticket.",
      clientSees: "Client sees “{{label}}”",
      notified: "client notified",
      notAnnounced: "not announced",
      needsReply: "Send a public reply first",
      needsManage: "Needs permission to manage tickets",
      resolveHint: "Asks for a resolution code",
      cancelHint: "Asks for a reason",
      moved: "Status set to {{status}}",
    },
  },
  sla: {
    label: "SLA clocks",
    none: "No SLA targets on this ticket",
    target: "Target: {{target}}",
    dueAt: "Due {{due}}",
    used: "{{used}} of the time used",
    warnsAt: "Warns at {{percent}}% elapsed",
    pausedWhy: "Paused: {{reason}}",
  },
  banners: {
    mergedTitle: "Merged into {{number}}.",
    mergedTitleUnknown: "Merged into another ticket.",
    mergedBody: "This ticket is read-only. Its messages were copied to the other ticket and the requester was added as a participant.",
    mergedOpen: "Open {{number}}",
    closedTitle: "Closed.",
    closedBody: "This ticket is read-only. A client reply within 30 days creates a follow-up ticket linked to it.",
    unverifiedTitle: "Unverified requester.",
    unverifiedBody: "{{name}} ({{email}}) has not verified this e-mail address, so there is no organisation, asset or order history on this ticket. Replies go to that address only.",
    noEmail: "no e-mail",
    verifyPhone: "Mark verified (phone call)",
    verifyTitle: "Mark as verified by phone?",
    verifyBody: "You confirm you spoke to {{name}} and checked who they are. This is recorded in the audit log and removes the unverified warning.",
    verifyConfirm: "Mark verified",
    verified: "Requester marked verified",
    breachedTitle: "SLA breached.",
    breachedBody: "{{metrics}} ran past the target. The breach is on the activity timeline.",
    resolvedTitle: "Resolved, waiting for {{name}} to confirm.",
    resolvedBody: "The ticket closes by itself if they don't answer.",
    reopen: "Reopen",
    collisionViewing: "{{names}} is viewing this ticket",
    collisionTyping: "{{names}} is viewing this ticket and is typing a reply",
    collisionBody: "Check the conversation before you send, so you don't answer twice.",
    dismiss: "Dismiss",
  },
  tabs: { label: "Ticket views", conversation: "Conversation", activity: "Activity", source: "Source chat" },
  filter: {
    label: "Filter the conversation",
    all: "All",
    public: "Public replies",
    internal: "Internal notes",
    system: "System",
    note: "Internal notes never reach the client.",
  },
  summary: {
    title: "AI summary",
    staffOnly: "staff only",
    generated: "generated {{when}}",
  },
  timeline: {
    client: "Client",
    staff: "Staff",
    assistant: "Assistant",
    publicChip: "Public reply",
    internalChip: "Internal note · staff only",
    replyBy: "Reply from {{name}}",
    noteBy: "Internal note from {{name}}",
    unknownAuthor: "Unknown",
    someone: "a colleague",
    edited: "edited",
    removed: "This message was removed by staff.",
    files: "Files sent with this message",
    openFile: "Open {{name}}",
    openFileFailed: "Couldn't open the file. Try again.",
    quote: "Quote",
    emptyTitle: "No messages match this filter",
    emptyBody: "Switch to All to see everything on the ticket.",
    showAll: "Show all",
    errorTitle: "We couldn't load the conversation",
    errorBody: "Check your connection and try again. Nothing was changed.",
  },
  activity: {
    title: "Activity timeline",
    note: "This is the ticket's own history, not the workspace audit log.",
    emptyTitle: "No activity yet",
    emptyBody: "Changes to the ticket (status, assignee, SLA, tags) appear here.",
    errorTitle: "We couldn't load the activity",
    errorBody: "Check your connection and try again. Nothing was changed.",
  },
  source: {
    title: "This ticket began in a chat",
    body: "The conversation with the assistant is in the Inbox. Open it there to read what was said before the ticket was raised.",
    open: "Open the conversation",
  },
  events: {
    ticket_created: "Ticket created.",
    route_applied: "Routed by “{{source}}”.",
    route_applied_plain: "Routed to a team.",
    status_changed: "Status: {{from}} → {{to}}.",
    status_changed_code: "Status: {{from}} → {{to}} (resolution: {{code}}).",
    assignee_changed: "Assigned to {{to}}.",
    assignee_changed_none: "Returned to the queue.",
    team_changed: "Team changed.",
    priority_changed: "Priority {{from}} → {{to}}. SLA targets recalculated.",
    type_changed: "Ticket type changed.",
    title_changed: "Title edited.",
    description_changed: "Description edited.",
    fields_changed: "Ticket fields updated ({{count}}).",
    tags_changed_added: "Tags added: {{added}}.",
    tags_changed_removed: "Tags removed: {{removed}}.",
    tags_changed_both: "Tags added: {{added}}; removed: {{removed}}.",
    due_changed: "Due date changed.",
    site_changed: "Site changed.",
    sla_attached: "SLA policy applied.",
    sla_recomputed: "SLA clocks recalculated.",
    sla_warning: "SLA warning: a target is close to its limit.",
    sla_breached: "SLA breached: a target ran past its limit.",
    follow_up_opened: "Follow-up ticket {{number}} opened.",
    ticket_reopened: "Ticket reopened.",
    collaborator_added: "{{name}} now follows the ticket.",
    collaborator_removed: "{{name}} stopped following the ticket.",
    csat_requested: "Rating requested from the client.",
    csat_received: "Client rated the ticket {{score}} / 5.",
    csat_skipped: "No rating asked for this ticket.",
    notify_client_email: "E-mail sent to the client.",
    notify_client_batched: "Client e-mail queued with others.",
    ticket_returned_to_queue: "Returned to the queue because nobody picked it up.",
    ticket_merged_source: "Merged into {{into}} ({{count}} messages copied).",
    ticket_merged_target: "{{from}} was merged into this ticket ({{count}} messages copied).",
    link_added: "Linked {{from}} and {{to}}.",
    link_removed: "Link between {{from}} and {{to}} removed.",
    macro_applied: "Macro “{{title}}” applied.",
    participant_added: "A participant was added.",
    participant_removed: "A participant was removed.",
    sharing_changed: "Sharing with the organisation changed.",
    spam_marked: "Marked as spam.",
    spam_cleared: "Spam mark removed.",
    ticket_deleted: "Ticket deleted.",
    ticket_restored: "Ticket restored.",
    timer_unassigned_due: "Still unassigned after the waiting time.",
    timer_waiting_reminder: "Reminder sent: waiting for the client.",
    timer_pending_expired: "Waited too long for the client.",
    timer_auto_close_due: "Auto-close time reached.",
    other: "Activity: {{type}}",
  },
  composer: {
    label: "Reply to the client or add a note",
    modes: "Reply or internal note",
    reply: "Reply",
    note: "Internal note",
    message: "Message",
    replyPlaceholder: "Write a reply to {{name}}…",
    notePlaceholder: "Add an internal note. @mention a colleague to notify them.",
    to: "To {{name}} <{{email}}> · {{channel}} + e-mail",
    noteTo: "Only staff can see this note. Use @ to mention a colleague.",
    clientFilipino: "Client language: Filipino",
    unverifiedWarn: "The requester is unverified. Your reply goes to {{email}} only and will not include order or asset details.",
    channel: { widget: "Widget", webapp: "Web app", mobile: "Mobile app", messenger: "Messenger", email: "E-mail", api: "API", console: "Console" },
    tools: { bold: "Bold", italic: "Italic", list: "Bulleted list", link: "Link" },
    macros: "Macros",
    attach: "Attach",
    attachInput: "Choose files to attach",
    mention: "Mention a colleague",
    mentionLabel: "Mention (internal notes only)",
    mentionNoteOnly: "Mentions work in internal notes",
    sends: "sends",
    sendReply: "Send reply",
    addNote: "Add note",
    sendAnd: "Send and set status",
    sendAndLabel: "Send reply and…",
    replyAndSet: "Reply & set {{status}}",
    replyAndResolve: "Reply & resolve…",
    replySent: "Reply sent",
    noteAdded: "Note added",
    sentMacro: "Reply sent · macro “{{title}}” applied",
    sentAndSet: "Reply sent · status {{status}}",
    errors: {
      emptyReply: "Write a reply before sending.",
      emptyNote: "Write a note before adding it.",
      scanning: "Wait for the virus scan to finish before sending.",
      macroFiles: "A macro can't carry files. Remove the files, or send the reply without the macro.",
    },
    offClosed: "Replies and notes are disabled on a closed ticket.",
    offMerged: "Replies and notes are disabled on a merged ticket.",
    offPermission: "You can read this ticket but not reply to it. Ask an admin for the reply permission.",
  },
  macros: {
    title: "Macros",
    search: "Search macros",
    also: "Also: {{actions}}",
    pending: "Macro: {{title}}",
    remove: "Remove macro actions",
    missing: "No value for {{variables}}: left empty, fill it in",
    inserted: "Macro inserted: {{title}}",
    emptyTitle: "No macros yet",
    emptyBody: "Macros are saved replies. Add them under Service settings.",
    noMatchTitle: "No macro matches",
    noMatchBody: "Try a word from its title or its shortcut.",
    errorTitle: "We couldn't load the macros",
    action: {
      set_status: "set status",
      set_priority: "set priority",
      set_type: "set type",
      add_tags: "add tags",
      remove_tags: "remove tags",
      assign: "assign",
      add_note: "add a note",
      set_field: "set a field",
    },
  },
  attach: {
    list: "Files to send with the message",
    uploading: "Uploading {{percent}}%",
    scanning: "Scanning…",
    clean: "Clean",
    blocked: "Blocked",
    failed: "Not sent",
    blockedBody: "This file was blocked for security.",
    failedBody: "The upload didn't finish. Try again.",
    retry: "Try again",
    remove: "Remove {{name}}",
    refused: {
      count: "A message can carry up to 10 files.",
      type: "This type of file isn't accepted. Use a photo, PDF, text, CSV or MP4 video.",
      size: "This file is over {{max}} MB.",
      empty: "This file is empty.",
    },
  },
  resolve: {
    title: "Resolve {{number}}",
    body: "The status becomes {{status}}. The client is asked to confirm.",
    code: "Resolution code",
    codeChoose: "Choose…",
    codeOptional: "Optional for this ticket type.",
    note: "Resolution note",
    noteHint: "What was done. The client sees this when you notify them.",
    notePlaceholder: "What was done to fix it",
    notify: "Notify the client",
    notifyHint: "Sends the note to the client as a reply. Your workflows then ask them to confirm and to rate us.",
    force: "Resolve even though child tickets are still open",
    submit: "Resolve",
    submitNotify: "Resolve & notify",
    done: "{{number}} resolved",
    errors: {
      code: "Choose a resolution code.",
      note: "Write what was done.",
      field: "This field is required to resolve.",
      children: "Child tickets of this ticket are still open. Close them first, or resolve anyway if you may.",
    },
  },
  cancel: {
    title: "Close as cancelled",
    body: "Only for duplicates, withdrawn requests, spam or out of scope.",
    reason: "Reason",
    note: "Note",
    keepOpen: "Keep it open",
    submit: "Close ticket",
    done: "{{number}} closed as cancelled",
    errors: { code: "Choose a reason." },
  },
  assign: {
    title: "Assign {{number}}",
    body: "Choose a team, then a person. The new owner is told.",
    me: "Assign to me",
    queue: "Return to queue",
    team: "Team",
    noTeam: "No team",
    person: "Person",
    nobody: "Nobody (the queue)",
    limited: "You can assign the ticket to yourself or return it to the queue. Picking someone else needs permission to see the team.",
    note: "Note to the new owner",
    notePlaceholder: "Optional. Added as an internal note.",
    submit: "Assign",
    done: "{{number}} assigned to {{name}}",
    returned: "{{number}} is back in the queue",
  },
  merge: {
    title: "Merge tickets",
    body: "Merging is irreversible. Both tickets must be unresolved and from the same organisation.",
    direction: "Direction",
    intoThis: "Merge another ticket into {{number}}",
    thisInto: "Merge {{number}} into another ticket",
    search: "Find a ticket",
    searchHint: "Type at least {{min}} characters of its number or title.",
    choose: "Choose a ticket",
    none: "No likely duplicates. Search by number or title.",
    likely: "Likely duplicate",
    blocked: { organisation: "Other organisation", resolved: "Already resolved" },
    preview: {
      copied_one: "{{count}} message from {{from}} is copied into {{into}} as a quoted block.",
      copied_other: "{{count}} messages from {{from}} are copied into {{into}} as a quoted block.",
      participant: "The requester of the merged ticket is added to {{into}} as a participant.",
      closed: "{{from}} is closed with resolution Duplicate and points to {{into}}.",
      irreversible: "This can't be undone.",
    },
    understood: "I understand this can't be undone.",
    submit: "Merge tickets",
    done: "{{from}} merged into {{into}}",
  },
  link: {
    title: "Link a ticket",
    body: "The link shows on both tickets.",
    relation: "Relationship",
    search: "Find a ticket",
    results: "Matching tickets",
    submit: "Link tickets",
    done: "Linked to {{number}}",
    removed: "Link to {{number}} removed",
    errors: { ticket: "Choose a ticket to link." },
    kinds: { related_to: "Related to", duplicate_of: "Duplicate of", caused_by: "Caused by (tracker)", blocks: "Blocks", blocked_by: "Blocked by" },
    relations: {
      related_to: "related to",
      duplicate_of: "duplicate of",
      duplicated_by: "duplicated by",
      caused_by: "caused by",
      causes: "causes",
      blocks: "blocks",
      blocked_by: "blocked by",
      follow_up_of: "follow-up of",
      followed_up_by: "followed up by",
    },
  },
  stale: {
    title: "This ticket changed",
    subtitle: "Someone saved changes while you were editing.",
    by: "{{name}} saved changes {{when}}.",
    byUnknown: "Someone saved changes just now.",
    hint: "Reload to see the latest version, or keep editing: what you typed stays in place and your next save uses the current version.",
    keep: "Keep my changes",
    reload: "Reload ticket",
  },
  ctx: {
    label: "Ticket context",
    requester: {
      title: "Requester and organisation",
      email: "E-mail",
      mobile: "Mobile",
      role: "Org role",
      sees: "Sees tickets",
      filipino: "Filipino",
      manager: "Account manager",
      openTickets: "Open tickets",
      openCount: "{{count}} ({{others}} others)",
      contractStub: "No contract on file yet",
      noOrganisation: "No organisation yet. Link the requester to a contact to see their organisation, contract and assets.",
      roles: { org_admin: "organisation admin", member: "member" },
      visibility: { own: "own", organisation: "the organisation's" },
      sentiment: { label: "Client tone", negative: "Upset or angry", neutral: "Neutral", positive: "Happy or grateful" },
    },
    links: {
      title: "Linked items",
      none: "Nothing linked yet.",
      error: "We couldn't load the links.",
      remove: "Remove link to {{number}}",
    },
    details: {
      title: "Details, tags and collaborators",
      type: "Type",
      team: "Team",
      channel: "Channel",
      created: "Created",
      updated: "Updated",
      language: "Language",
      english: "English",
      tags: "Tags",
      noTags: "No tags yet.",
      addTag: "Add a tag",
      add: "Add",
      tagInvalid: "Use lowercase letters, numbers and dashes, up to {{max}} characters.",
      tagLimit: "A ticket can have at most {{max}} tags.",
      tagAdded: "Tag “{{tag}}” added",
      tagRemoved: "Tag “{{tag}}” removed",
      tagRemove: "Remove tag {{tag}}",
      due: "Due date",
      dueHint: "Optional, separate from the SLA.",
      dueSet: "Due date set to {{date}}",
      dueCleared: "Due date cleared",
      collaborators: "Collaborators",
      nobody: "Nobody yet",
      addColleague: "Add a colleague…",
      collaboratorRemove: "Remove {{name}} as collaborator",
    },
    fields: {
      title: "Ticket fields",
      none: "{{type}} has no custom fields. Add them in the ticket type settings.",
      save: "Save fields",
      saved: "Fields saved",
      invalid: "This answer isn't valid.",
      error: "We couldn't load the fields of this ticket type.",
      requiredOnResolve: "required to resolve",
    },
    csat: {
      title: "CSAT",
      stars: "{{score}} out of 5 stars",
      rated: "Rated {{when}}",
      asked: "Rating requested. No answer yet.",
      skipped: "No rating was asked for this ticket.",
      pending: "A rating is requested when the ticket is resolved.",
      info: "A 1 to 5 star rating is requested from the requester once the ticket is resolved.",
    },
  },
} as const;
